import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { EventTypes } from 'src/app/core/constants/event-types';
import { AppEditSendReceiversDeliveryStatus } from 'src/app/core/types/app-edit-send-receivers-delivery-status.type';
import { BaseFormPageComponent } from 'src/app/pages/shared/base/components/base-form-page.component';
import {
    GetEnumValuesDto,
    OutboxMessageNumbersCountByDeliveryStatusDto,
} from '../../../../core/api/models';
import { DeliveryStatus } from '../../../../core/api/models/delivery-status';
import { OutboxMessageNumbersService } from '../../../../core/api/services/outbox-message-numbers.service';
import { DialogService } from '../../../../core/services/dialog.service';
import { TranslationService } from '../../../../i18n/translation.service';

@Component({
    selector: 'app-edit-send-receivers-delivery-status',
    templateUrl: './edit-send-receivers-delivery-status.component.html',
    styleUrls: ['./edit-send-receivers-delivery-status.component.scss'],
    standalone: false
})
export class EditSendReceiversDeliveryStatus
  extends BaseFormPageComponent
  implements OnInit
{
  //#region Properties

  scheduledMessageId: number;

  type: AppEditSendReceiversDeliveryStatus = 'selective';

  newStatus: DeliveryStatus;
  oldStatus: DeliveryStatus;

  receivers: string;

  DeliveryStatusEnumValues: GetEnumValuesDto[] = [];
  items: OutboxMessageNumbersCountByDeliveryStatusDto[] = [];

  get DeliveryStatus() {
    return DeliveryStatus;
  }

  //#endregion
  constructor(
    public translationService: TranslationService,
    protected currentService: OutboxMessageNumbersService,
    public dialogService: DialogService,

    private dialogRef: MatDialogRef<EditSendReceiversDeliveryStatus>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    super();
    if (data) {
      this.scheduledMessageId = data.scheduledMessageId;
      this.receivers = data.receivers;
      this.DeliveryStatusEnumValues = data.DeliveryStatusEnumValues;
      this.type = data.type;
    }
  }

  ngOnInit() {
    try {
      this.getExistStatusesApi();
      this.initForm();

      switch (this.type) {
        case 'getNewDeliveryStatus':
          this.formPageConfig.title =
            'pages.outboxMessages.getNewDeliveryStatusTitle';
          break;
        case 'byFile':
          this.formPageConfig.title = 'common.editDeliveryStatusByExcelFile';
          break;
        case 'all':
          this.formPageConfig.title = 'common.editStatusAllReceivers';
          break;
        default:
          this.formPageConfig.title = 'pages.outboxMessages.editStatusTitle';
          break;
      }
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.smsReport);
    }
  }

  ngAfterViewInit(): void {
    this.cd.detectChanges();
  }

  //#region init

  initForm() {
    this.form = new FormGroup({
      oldStatus: new FormControl('', Validators.required),
      newStatus: new FormControl('', Validators.required),
      file: new FormControl(''),
    });

    if (this.type == 'byFile') {
      this.form.get('file').setValidators([Validators.required]);
      this.form.get('file').updateValueAndValidity();
    }
    if (this.type == 'selective' || this.type == 'getNewDeliveryStatus') {
      this.form.get('oldStatus').clearValidators();
      this.form.get('oldStatus').updateValueAndValidity();
    }
  }

  //#endregion

  //#region Api

  getExistStatusesApi() {
    this.unSubs();
    this.loading = true;
    this.currentService
      .outboxMessageNumbersGetOutboxMessageDeliveryStatusStatisticsByScheduledMessageId(
        {
          body: { scheduledMessageId: this.scheduledMessageId },
        }
      )
      .pipe(this.basePipeOperators())
      .subscribe((res) => {
        try {
          if (res) {
            this.items = res.data;
          }
        } catch (error) {
          this.loggerService.logError(
            this.tagName,
            error,
            EventTypes.smsReport
          );
        }
      });
  }

  outBoxMessagesUpdateStatusApi() {
    if (!this.receivers) return;

    this.unSubs();
    this.loading = true;

    this.currentService
      .outboxMessageNumbersUpdateOutBoxMessageNumbersDeliveryStatusPost({
        body: {
          scheduledMessageId: this.scheduledMessageId,
          deliveryStatus: this.newStatus,
          numbers: this.compressionString(this.receivers),
        },
      })
      .pipe(this.basePipeOperators())
      .subscribe(this.baseObserver);
  }

  updateSelectedOutBoxMessageNumbersDeliveryStatusApi() {
    if (!this.receivers) return;

    this.unSubs();
    this.loading = true;

    this.currentService
      .outboxMessageNumbersUpdateSelectedOutBoxMessageNumbersDeliveryStatusPost(
        {
          body: {
            scheduledMessageId: this.scheduledMessageId,
            deliveryStatus: this.newStatus,
            numbers: this.compressionString(this.receivers),
          },
        }
      )
      .pipe(this.basePipeOperators())
      .subscribe(this.baseObserver);
  }

  updateDeliveryStatusByDeliveryStatusAndScheduledMessageIdApi() {
    this.unSubs();
    this.loading = true;

    this.currentService
      .outboxMessageNumbersUpdateDeliveryStatusByDeliveryStatusAndScheduledMessageIdPost(
        {
          body: {
            scheduledMessageId: this.scheduledMessageId,
            newStatus: this.newStatus,
            oldStatus: this.oldStatus,
          },
        }
      )
      .pipe(this.basePipeOperators())
      .subscribe(this.baseObserver);
  }

  applyingToGetNewDeliveryStatusApi() {
    this.unSubs();
    this.loading = true;

    this.currentService
      .outboxMessageNumbersApplyingToGetNewDeliveryStatusPost({
        body: {
          deliveryStatus: this.newStatus,
          scheduledMessageId: this.scheduledMessageId,
        },
      })
      .pipe(this.basePipeOperators())
      .subscribe(this.baseObserver);
  }

  //#endregion

  onSubmit() {
    try {
      if (!this.isValidForm(this.form, this.tagName)) return;
      if (this.loading) return;

      switch (this.type) {
        case 'selective':
          this.updateSelectedOutBoxMessageNumbersDeliveryStatusApi();
          break;
        case 'byFile':
          this.outBoxMessagesUpdateStatusApi();
          break;
        case 'all':
          this.updateDeliveryStatusByDeliveryStatusAndScheduledMessageIdApi();
          break;
        case 'getNewDeliveryStatus':
          this.applyingToGetNewDeliveryStatusApi();
          break;
        default:
          break;
      }
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.smsReport);
    }
  }

  onClose() {
    this.dialogRef.close();
  }

  onSuccess() {
    this.dialogRef.close(true);
    this.unSubs();
  }

  unSubs() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
